import { SshMessageReader } from '../protocol/message-reader.js';
import { SshMessageWriter } from '../protocol/message-writer.js';
import { bcryptPbkdf } from './bcrypt-pbkdf.js';

const OPENSSH_KEY_V1_MAGIC = 'openssh-key-v1\0';

/**
 * A parsed SSH private key ready for signing.
 */
export type SshPrivateKey = {
	/** algorithm name used in the public key blob (e.g. "ssh-ed25519", "ssh-rsa") */
	algorithm: string;
	/** algorithm name used for signing (e.g. "ssh-ed25519", "rsa-sha2-256") */
	signingAlgorithm: string;
	publicKeyBlob: Uint8Array;
	signingKey: CryptoKey;
};

/**
 * Parse a PEM-encoded or OpenSSH-format private key.
 * Supports Ed25519 and RSA keys in OpenSSH, PKCS#1, PKCS#8, and encrypted variants.
 *
 * @param keyData The private key as a string (PEM format)
 * @param passphrase Optional passphrase for encrypted keys
 */
export async function parsePrivateKey(
	keyData: string,
	passphrase?: string,
): Promise<SshPrivateKey> {
	const trimmed = keyData.trim();

	if (trimmed.startsWith('-----BEGIN OPENSSH PRIVATE KEY-----')) {
		return parseOpenSshKey(trimmed, passphrase);
	}

	if (trimmed.startsWith('-----BEGIN RSA PRIVATE KEY-----')) {
		return parsePkcs1RsaKey(trimmed, passphrase);
	}

	if (trimmed.startsWith('-----BEGIN ENCRYPTED PRIVATE KEY-----')) {
		return parseEncryptedPkcs8Key(trimmed, passphrase);
	}

	if (trimmed.startsWith('-----BEGIN PRIVATE KEY-----')) {
		return parsePkcs8Key(trimmed);
	}

	throw new Error('Unsupported private key format. Supported: OpenSSH, PKCS#1 (RSA), PKCS#8.');
}

/**
 * Sign data with an SSH private key and return the signature in SSH format.
 *
 * The signature blob is:
 *   string  algorithm-name
 *   string  signature-bytes
 */
export async function signWithKey(key: SshPrivateKey, data: Uint8Array): Promise<Uint8Array> {
	let sig: ArrayBuffer;

	if (key.signingAlgorithm === 'ssh-ed25519') {
		sig = await crypto.subtle.sign('Ed25519', key.signingKey, new Uint8Array(data));
	} else if (key.signingAlgorithm === 'rsa-sha2-256') {
		sig = await crypto.subtle.sign(
			{ name: 'RSASSA-PKCS1-v1_5' },
			key.signingKey,
			new Uint8Array(data),
		);
	} else if (key.signingAlgorithm === 'rsa-sha2-512') {
		sig = await crypto.subtle.sign(
			{ name: 'RSASSA-PKCS1-v1_5' },
			key.signingKey,
			new Uint8Array(data),
		);
	} else {
		throw new Error(`Unsupported signing algorithm: ${key.signingAlgorithm}`);
	}

	const w = new SshMessageWriter();
	w.writeString(key.signingAlgorithm);
	w.writeString(new Uint8Array(sig));
	return w.toUint8Array();
}

// -- OpenSSH format (openssh-key-v1) -----------------------------------

/**
 * Parse an OpenSSH-format private key (openssh-key-v1).
 *
 * Format:
 *   "openssh-key-v1\0"  (magic, 15 bytes including null)
 *   string ciphername   ("none" for unencrypted)
 *   string kdfname      ("none" for unencrypted)
 *   string kdfoptions   (empty for unencrypted)
 *   uint32 number_of_keys (always 1)
 *   string public_key_blob
 *   string private_section (encrypted or plain)
 *
 * Private section (unencrypted):
 *   uint32 checkint1
 *   uint32 checkint2    (must equal checkint1)
 *   string keytype
 *   ... key-type specific data ...
 *   string comment
 *   padding (1, 2, 3, ... repeating)
 */
async function parseOpenSshKey(pem: string, passphrase?: string): Promise<SshPrivateKey> {
	const raw = decodePemBody(pem);

	// verify magic
	const magicBytes = raw.subarray(0, OPENSSH_KEY_V1_MAGIC.length);
	const magic = new TextDecoder().decode(magicBytes);
	if (magic !== OPENSSH_KEY_V1_MAGIC) {
		throw new Error('Invalid OpenSSH private key: bad magic');
	}

	const reader = new SshMessageReader(raw.subarray(OPENSSH_KEY_V1_MAGIC.length));

	const cipherName = reader.readStringAsText();
	const kdfName = reader.readStringAsText();
	const kdfOptions = reader.readString();
	const numKeys = reader.readUint32();

	if (numKeys !== 1) {
		throw new Error(`Expected 1 key, got ${numKeys}`);
	}

	// public key blob
	const publicKeyBlob = reader.readString();

	// private section (may be encrypted)
	let privateSection = reader.readString();

	if (cipherName !== 'none' || kdfName !== 'none') {
		if (!passphrase) {
			throw new Error('Private key is encrypted but no passphrase was provided');
		}

		privateSection = await decryptOpenSshPrivateSection(
			privateSection,
			cipherName,
			kdfName,
			kdfOptions,
			passphrase,
		);
	}

	const privReader = new SshMessageReader(privateSection);

	// check ints (must match)
	const checkInt1 = privReader.readUint32();
	const checkInt2 = privReader.readUint32();
	if (checkInt1 !== checkInt2) {
		throw new Error('Private key checkint mismatch (wrong passphrase or corrupt key)');
	}

	// key type
	const keyType = privReader.readStringAsText();

	if (keyType === 'ssh-ed25519') {
		return parseEd25519PrivateKey(privReader, publicKeyBlob);
	}

	if (keyType === 'ssh-rsa') {
		return parseOpenSshRsaPrivateKey(privReader, publicKeyBlob);
	}

	throw new Error(`Unsupported private key type: ${keyType}`);
}

// -- OpenSSH encrypted private section decryption -------------------------

/**
 * Cipher info for OpenSSH key encryption.
 */
type OpenSshCipherInfo = {
	keyLen: number;
	ivLen: number;
	blockSize: number;
	algorithm: string;
};

const OPENSSH_CIPHERS: Record<string, OpenSshCipherInfo> = {
	'aes256-ctr': { keyLen: 32, ivLen: 16, blockSize: 16, algorithm: 'AES-CTR' },
	'aes256-cbc': { keyLen: 32, ivLen: 16, blockSize: 16, algorithm: 'AES-CBC' },
	'aes128-ctr': { keyLen: 16, ivLen: 16, blockSize: 16, algorithm: 'AES-CTR' },
	'aes128-cbc': { keyLen: 16, ivLen: 16, blockSize: 16, algorithm: 'AES-CBC' },
	'aes192-ctr': { keyLen: 24, ivLen: 16, blockSize: 16, algorithm: 'AES-CTR' },
	'aes192-cbc': { keyLen: 24, ivLen: 16, blockSize: 16, algorithm: 'AES-CBC' },
};

/**
 * Decrypt an OpenSSH encrypted private section using bcrypt-pbkdf.
 */
async function decryptOpenSshPrivateSection(
	encrypted: Uint8Array,
	cipherName: string,
	kdfName: string,
	kdfOptions: Uint8Array,
	passphrase: string,
): Promise<Uint8Array> {
	if (kdfName !== 'bcrypt') {
		throw new Error(`Unsupported KDF: ${kdfName}. Only bcrypt is supported.`);
	}

	const cipherInfo = OPENSSH_CIPHERS[cipherName];
	if (!cipherInfo) {
		throw new Error(
			`Unsupported cipher: ${cipherName}. Supported: ${Object.keys(OPENSSH_CIPHERS).join(', ')}`,
		);
	}

	// parse kdf options: string salt, uint32 rounds
	const kdfReader = new SshMessageReader(kdfOptions);
	const salt = kdfReader.readString();
	const rounds = kdfReader.readUint32();

	// derive key material using bcrypt-pbkdf
	const passBytes = new TextEncoder().encode(passphrase);
	const derivedLen = cipherInfo.keyLen + cipherInfo.ivLen;
	const derived = await bcryptPbkdf(passBytes, salt, derivedLen, rounds);

	const key = derived.subarray(0, cipherInfo.keyLen);
	const iv = derived.subarray(cipherInfo.keyLen, derivedLen);

	// import key and decrypt
	const cryptoKey = await crypto.subtle.importKey(
		'raw',
		new Uint8Array(key),
		cipherInfo.algorithm,
		false,
		['decrypt'],
	);

	let decrypted: ArrayBuffer;
	if (cipherInfo.algorithm === 'AES-CTR') {
		decrypted = await crypto.subtle.decrypt(
			{ name: 'AES-CTR', counter: new Uint8Array(iv), length: 128 },
			cryptoKey,
			new Uint8Array(encrypted),
		);
	} else {
		decrypted = await crypto.subtle.decrypt(
			{ name: 'AES-CBC', iv: new Uint8Array(iv) },
			cryptoKey,
			new Uint8Array(encrypted),
		);
	}

	return new Uint8Array(decrypted);
}

// -- Ed25519 -----------------------------------------------------------

/**
 * Parse an Ed25519 private key from the openssh-key-v1 private section.
 *
 * Ed25519 private section format (after keytype):
 *   string  public_key (32 bytes)
 *   string  private_key (64 bytes: 32-byte seed + 32-byte public key)
 */
async function parseEd25519PrivateKey(
	reader: SshMessageReader,
	publicKeyBlob: Uint8Array,
): Promise<SshPrivateKey> {
	reader.readString(); // public key (we already have it from the blob)
	const privateKeyRaw = reader.readString(); // 64 bytes: seed (32) + pubkey (32)

	// the seed is the first 32 bytes
	const seed = privateKeyRaw.subarray(0, 32);

	// import as Ed25519 private key via PKCS#8 wrapping
	const pkcs8 = wrapEd25519SeedAsPkcs8(seed);

	const signingKey = await crypto.subtle.importKey(
		'pkcs8',
		new Uint8Array(pkcs8),
		'Ed25519',
		false,
		['sign'],
	);

	return {
		algorithm: 'ssh-ed25519',
		signingAlgorithm: 'ssh-ed25519',
		publicKeyBlob,
		signingKey,
	};
}

/**
 * Wrap a 32-byte Ed25519 seed into PKCS#8 DER format for Web Crypto import.
 *
 * PKCS#8 structure for Ed25519:
 *   SEQUENCE {
 *     INTEGER 0 (version)
 *     SEQUENCE {
 *       OID 1.3.101.112 (Ed25519)
 *     }
 *     OCTET STRING {
 *       OCTET STRING (32 bytes seed)
 *     }
 *   }
 */
function wrapEd25519SeedAsPkcs8(seed: Uint8Array): Uint8Array {
	const prefix = new Uint8Array([
		0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
	]);

	const pkcs8 = new Uint8Array(prefix.length + 32);
	pkcs8.set(prefix);
	pkcs8.set(seed, prefix.length);
	return pkcs8;
}

// -- RSA (OpenSSH format) ----------------------------------------------

/**
 * Parse an RSA private key from the openssh-key-v1 private section.
 *
 * RSA private section format (after keytype "ssh-rsa"):
 *   mpint  n        (modulus)
 *   mpint  e        (public exponent)
 *   mpint  d        (private exponent)
 *   mpint  iqmp     (CRT coefficient: q^-1 mod p)
 *   mpint  p        (first prime)
 *   mpint  q        (second prime)
 *   string comment
 *   padding
 */
async function parseOpenSshRsaPrivateKey(
	reader: SshMessageReader,
	publicKeyBlob: Uint8Array,
): Promise<SshPrivateKey> {
	const n = reader.readMpint();
	const e = reader.readMpint();
	const d = reader.readMpint();
	const iqmp = reader.readMpint();
	const p = reader.readMpint();
	const q = reader.readMpint();

	// derive dp = d mod (p-1), dq = d mod (q-1)
	const dp = d % (p - 1n);
	const dq = d % (q - 1n);

	return importRsaKey(n, e, d, p, q, dp, dq, iqmp, publicKeyBlob);
}

// -- RSA (PEM PKCS#1 format) -------------------------------------------

/**
 * Parse a PEM PKCS#1 RSA private key (-----BEGIN RSA PRIVATE KEY-----).
 * Supports both unencrypted and encrypted (Proc-Type/DEK-Info) variants.
 *
 * PKCS#1 RSAPrivateKey DER structure (RFC 8017):
 *   SEQUENCE {
 *     INTEGER version (0)
 *     INTEGER n
 *     INTEGER e
 *     INTEGER d
 *     INTEGER p
 *     INTEGER q
 *     INTEGER dp   (d mod p-1)
 *     INTEGER dq   (d mod q-1)
 *     INTEGER qi   (q^-1 mod p)
 *   }
 */
async function parsePkcs1RsaKey(pem: string, passphrase?: string): Promise<SshPrivateKey> {
	const lines = pem.split('\n');
	let der: Uint8Array;

	// check for Proc-Type: 4,ENCRYPTED header (traditional OpenSSL encrypted PEM)
	const procTypeLine = lines.find((l) => l.startsWith('Proc-Type:'));
	const dekInfoLine = lines.find((l) => l.startsWith('DEK-Info:'));

	if (procTypeLine && dekInfoLine) {
		if (!passphrase) {
			throw new Error('Private key is encrypted but no passphrase was provided');
		}
		der = await decryptTraditionalPem(lines, dekInfoLine, passphrase);
	} else {
		der = decodePemBody(pem);
	}

	const values = parsePkcs1Der(der);

	const [_version, n, e, d, p, q, dp, dq, qi] = values;
	if (values.length < 9) {
		throw new Error('Invalid PKCS#1 RSA key: expected 9 integers');
	}

	const publicKeyBlob = buildRsaPublicKeyBlob(e!, n!);
	return importRsaKey(n!, e!, d!, p!, q!, dp!, dq!, qi!, publicKeyBlob);
}

// -- RSA (PEM PKCS#8 unencrypted format) --------------------------------

/**
 * Parse a PEM PKCS#8 private key (-----BEGIN PRIVATE KEY-----).
 * Extracts the RSA parameters from the nested PKCS#1 structure inside PKCS#8.
 *
 * PKCS#8 PrivateKeyInfo DER structure:
 *   SEQUENCE {
 *     INTEGER version (0)
 *     SEQUENCE {
 *       OID  algorithm (1.2.840.113549.1.1.1 = rsaEncryption)
 *       NULL
 *     }
 *     OCTET STRING {
 *       <PKCS#1 RSAPrivateKey>
 *     }
 *   }
 */
async function parsePkcs8Key(pem: string): Promise<SshPrivateKey> {
	const der = decodePemBody(pem);
	return parsePkcs8DerContent(der);
}

// -- PKCS#8 encrypted format (EncryptedPrivateKeyInfo) --------------------

/**
 * Known OIDs for PKCS#8 encryption.
 */
const PBES2_OID = new Uint8Array([0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x05, 0x0d]); // 1.2.840.113549.1.5.13
const PBKDF2_OID = new Uint8Array([0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x05, 0x0c]); // 1.2.840.113549.1.5.12
const HMAC_SHA256_OID = new Uint8Array([0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x02, 0x09]); // 1.2.840.113549.2.9
const HMAC_SHA1_OID = new Uint8Array([0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x02, 0x07]); // 1.2.840.113549.2.7
const AES256_CBC_OID = new Uint8Array([0x60, 0x86, 0x48, 0x01, 0x65, 0x03, 0x04, 0x01, 0x2a]); // 2.16.840.1.101.3.4.1.42
const AES128_CBC_OID = new Uint8Array([0x60, 0x86, 0x48, 0x01, 0x65, 0x03, 0x04, 0x01, 0x02]); // 2.16.840.1.101.3.4.1.2
const AES192_CBC_OID = new Uint8Array([0x60, 0x86, 0x48, 0x01, 0x65, 0x03, 0x04, 0x01, 0x16]); // 2.16.840.1.101.3.4.1.22

/**
 * Parse a PKCS#8 encrypted private key (-----BEGIN ENCRYPTED PRIVATE KEY-----).
 *
 * EncryptedPrivateKeyInfo DER structure:
 *   SEQUENCE {
 *     SEQUENCE { <encryption algorithm: PBES2> }
 *     OCTET STRING { <encrypted data> }
 *   }
 */
async function parseEncryptedPkcs8Key(pem: string, passphrase?: string): Promise<SshPrivateKey> {
	if (!passphrase) {
		throw new Error('Private key is encrypted but no passphrase was provided');
	}

	const der = decodePemBody(pem);
	const outer = derReadSequence(der, 0);
	let pos = outer.contentStart;

	// read encryption algorithm SEQUENCE
	const algSeq = derReadSequence(der, pos);
	pos = algSeq.end;

	// read encrypted data OCTET STRING
	const encDataEl = derReadElement(der, pos);
	if (encDataEl.tag !== 0x04) {
		throw new Error('Expected OCTET STRING for encrypted data in PKCS#8');
	}
	const encryptedData = der.subarray(encDataEl.contentStart, encDataEl.end);

	// parse the encryption algorithm — we only support PBES2
	const { key, iv, algorithm } = await parsePbes2(der, algSeq, passphrase);

	// decrypt
	const cryptoKey = await crypto.subtle.importKey('raw', new Uint8Array(key), algorithm, false, [
		'decrypt',
	]);

	const decrypted = await crypto.subtle.decrypt(
		{ name: algorithm, iv: new Uint8Array(iv) },
		cryptoKey,
		new Uint8Array(encryptedData),
	);

	// the decrypted content is a PKCS#8 PrivateKeyInfo — parse it like unencrypted PKCS#8
	const pkcs8Der = new Uint8Array(decrypted);
	return parsePkcs8DerContent(pkcs8Der);
}

/**
 * Parse PBES2 algorithm parameters from an AlgorithmIdentifier SEQUENCE.
 * Returns the derived key, IV, and Web Crypto algorithm name.
 */
async function parsePbes2(
	der: Uint8Array,
	algSeq: DerElement,
	passphrase: string,
): Promise<{ key: Uint8Array; iv: Uint8Array; algorithm: string }> {
	let pos = algSeq.contentStart;

	// read OID — must be PBES2
	const oidEl = derReadElement(der, pos);
	if (oidEl.tag !== 0x06) {
		throw new Error('Expected OID in encryption algorithm');
	}
	const oid = der.subarray(oidEl.contentStart, oidEl.end);
	if (!arraysEqual(oid, PBES2_OID)) {
		throw new Error('Unsupported PKCS#8 encryption. Only PBES2 is supported.');
	}
	pos = oidEl.end;

	// PBES2 params SEQUENCE
	const pbes2Params = derReadSequence(der, pos);
	let paramPos = pbes2Params.contentStart;

	// KDF SEQUENCE (PBKDF2)
	const kdfSeq = derReadSequence(der, paramPos);
	paramPos = kdfSeq.end;

	// encryption scheme SEQUENCE
	const encSeq = derReadSequence(der, paramPos);

	// parse KDF
	let kdfPos = kdfSeq.contentStart;
	const kdfOidEl = derReadElement(der, kdfPos);
	const kdfOid = der.subarray(kdfOidEl.contentStart, kdfOidEl.end);
	if (!arraysEqual(kdfOid, PBKDF2_OID)) {
		throw new Error('Unsupported KDF in PBES2. Only PBKDF2 is supported.');
	}
	kdfPos = kdfOidEl.end;

	// PBKDF2 params SEQUENCE
	const pbkdf2Params = derReadSequence(der, kdfPos);
	let pbPos = pbkdf2Params.contentStart;

	// salt OCTET STRING
	const saltEl = derReadElement(der, pbPos);
	if (saltEl.tag !== 0x04) {
		throw new Error('Expected OCTET STRING for PBKDF2 salt');
	}
	const salt = der.subarray(saltEl.contentStart, saltEl.end);
	pbPos = saltEl.end;

	// iteration count INTEGER
	const iterEl = derReadElement(der, pbPos);
	if (iterEl.tag !== 0x02) {
		throw new Error('Expected INTEGER for PBKDF2 iteration count');
	}
	let iterations = 0;
	for (let i = iterEl.contentStart; i < iterEl.end; i++) {
		iterations = (iterations << 8) | der[i]!;
	}
	pbPos = iterEl.end;

	// optional key length INTEGER (skip if present)
	if (pbPos < pbkdf2Params.end) {
		const nextEl = derReadElement(der, pbPos);
		if (nextEl.tag === 0x02) {
			// key length — skip
			pbPos = nextEl.end;
		}
	}

	// PRF AlgorithmIdentifier (optional — defaults to HMAC-SHA1)
	let hashAlgorithm = 'SHA-1';
	if (pbPos < pbkdf2Params.end) {
		const prfSeq = derReadSequence(der, pbPos);
		const prfOidEl = derReadElement(der, prfSeq.contentStart);
		const prfOid = der.subarray(prfOidEl.contentStart, prfOidEl.end);
		if (arraysEqual(prfOid, HMAC_SHA256_OID)) {
			hashAlgorithm = 'SHA-256';
		} else if (arraysEqual(prfOid, HMAC_SHA1_OID)) {
			hashAlgorithm = 'SHA-1';
		} else {
			throw new Error('Unsupported PRF in PBKDF2. Only HMAC-SHA-1 and HMAC-SHA-256 are supported.');
		}
	}

	// parse encryption scheme
	let encPos = encSeq.contentStart;
	const encOidEl = derReadElement(der, encPos);
	const encOid = der.subarray(encOidEl.contentStart, encOidEl.end);
	encPos = encOidEl.end;

	let keyLen: number;
	const algorithm = 'AES-CBC';
	if (arraysEqual(encOid, AES256_CBC_OID)) {
		keyLen = 32;
	} else if (arraysEqual(encOid, AES128_CBC_OID)) {
		keyLen = 16;
	} else if (arraysEqual(encOid, AES192_CBC_OID)) {
		keyLen = 24;
	} else {
		throw new Error('Unsupported encryption scheme in PBES2. Only AES-CBC is supported.');
	}

	// IV OCTET STRING
	const ivEl = derReadElement(der, encPos);
	if (ivEl.tag !== 0x04) {
		throw new Error('Expected OCTET STRING for encryption IV');
	}
	const iv = der.subarray(ivEl.contentStart, ivEl.end);

	// derive key using PBKDF2 via Web Crypto
	const passBytes = new TextEncoder().encode(passphrase);
	const baseKey = await crypto.subtle.importKey('raw', new Uint8Array(passBytes), 'PBKDF2', false, [
		'deriveBits',
	]);

	const derivedBits = await crypto.subtle.deriveBits(
		{
			name: 'PBKDF2',
			salt: new Uint8Array(salt),
			iterations,
			hash: hashAlgorithm,
		},
		baseKey,
		keyLen * 8,
	);

	return {
		key: new Uint8Array(derivedBits),
		iv: new Uint8Array(iv),
		algorithm,
	};
}

/**
 * Parse a PKCS#8 PrivateKeyInfo DER structure (used by both encrypted and unencrypted paths).
 */
async function parsePkcs8DerContent(der: Uint8Array): Promise<SshPrivateKey> {
	const outer = derReadSequence(der, 0);
	let pos = outer.contentStart;

	// skip version INTEGER
	const version = derReadElement(der, pos);
	pos = version.end;

	// read AlgorithmIdentifier SEQUENCE
	const algSeq = derReadSequence(der, pos);
	pos = algSeq.end;

	// read OID from AlgorithmIdentifier
	const oidElement = derReadElement(der, algSeq.contentStart);
	if (oidElement.tag !== 0x06) {
		throw new Error('Expected OID in PKCS#8 AlgorithmIdentifier');
	}
	const oidBytes = der.subarray(oidElement.contentStart, oidElement.end);

	// check for rsaEncryption OID: 1.2.840.113549.1.1.1
	const rsaOid = new Uint8Array([0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x01, 0x01]);
	if (!arraysEqual(oidBytes, rsaOid)) {
		const ed25519Oid = new Uint8Array([0x2b, 0x65, 0x70]);
		if (arraysEqual(oidBytes, ed25519Oid)) {
			throw new Error('Ed25519 PKCS#8 keys are not supported. Use OpenSSH format instead.');
		}
		throw new Error('Unsupported PKCS#8 key algorithm. Only RSA is supported.');
	}

	// read private key OCTET STRING containing PKCS#1 RSAPrivateKey
	const privKeyOctet = derReadElement(der, pos);
	if (privKeyOctet.tag !== 0x04) {
		throw new Error('Expected OCTET STRING for private key in PKCS#8');
	}
	const pkcs1Der = der.subarray(privKeyOctet.contentStart, privKeyOctet.end);

	const values = parsePkcs1Der(pkcs1Der);
	if (values.length < 9) {
		throw new Error('Invalid RSA key in PKCS#8: expected 9 integers');
	}

	const [_version2, n, e, d, p, q, dp, dq, qi] = values;
	const publicKeyBlob = buildRsaPublicKeyBlob(e!, n!);
	return importRsaKey(n!, e!, d!, p!, q!, dp!, dq!, qi!, publicKeyBlob);
}

// -- Traditional PEM encrypted format (Proc-Type/DEK-Info) ----------------

/**
 * DEK-Info cipher specifications for traditional PEM encryption.
 */
type DekCipherInfo = {
	keyLen: number;
	algorithm: string;
};

const DEK_CIPHERS: Record<string, DekCipherInfo> = {
	'AES-128-CBC': { keyLen: 16, algorithm: 'AES-CBC' },
	'AES-192-CBC': { keyLen: 24, algorithm: 'AES-CBC' },
	'AES-256-CBC': { keyLen: 32, algorithm: 'AES-CBC' },
	'DES-EDE3-CBC': { keyLen: 24, algorithm: 'DES-EDE3-CBC' }, // not supported by Web Crypto
};

/**
 * Decrypt a traditional PEM-encrypted key (Proc-Type: 4,ENCRYPTED with DEK-Info header).
 * Uses OpenSSL's EVP_BytesToKey-derived key derivation (MD5-based).
 */
async function decryptTraditionalPem(
	lines: string[],
	dekInfoLine: string,
	passphrase: string,
): Promise<Uint8Array> {
	// parse DEK-Info: CIPHER,IV-HEX
	const dekMatch = dekInfoLine.match(/^DEK-Info:\s*(\S+),([0-9a-fA-F]+)/);
	if (!dekMatch) {
		throw new Error('Invalid DEK-Info header');
	}

	const cipherName = dekMatch[1]!;
	const ivHex = dekMatch[2]!;

	const cipherInfo = DEK_CIPHERS[cipherName];
	if (!cipherInfo) {
		throw new Error(
			`Unsupported DEK cipher: ${cipherName}. Supported: ${Object.keys(DEK_CIPHERS).join(', ')}`,
		);
	}

	if (cipherInfo.algorithm === 'DES-EDE3-CBC') {
		throw new Error('DES-EDE3-CBC is not supported by Web Crypto API. Use AES-encrypted keys.');
	}

	const iv = hexDecode(ivHex);

	// derive key using EVP_BytesToKey with MD5
	const passBytes = new TextEncoder().encode(passphrase);
	const key = evpBytesToKey(passBytes, iv.subarray(0, 8), cipherInfo.keyLen);

	// extract the base64 body (skip header lines)
	const bodyLines = lines.filter(
		(l) =>
			!l.startsWith('-----BEGIN') &&
			!l.startsWith('-----END') &&
			!l.startsWith('Proc-Type:') &&
			!l.startsWith('DEK-Info:') &&
			l.trim().length > 0,
	);
	const encrypted = base64Decode(bodyLines.join(''));

	// decrypt
	const cryptoKey = await crypto.subtle.importKey(
		'raw',
		new Uint8Array(key),
		cipherInfo.algorithm,
		false,
		['decrypt'],
	);

	const decrypted = await crypto.subtle.decrypt(
		{ name: cipherInfo.algorithm, iv: new Uint8Array(iv) },
		cryptoKey,
		new Uint8Array(encrypted),
	);

	return new Uint8Array(decrypted);
}

/**
 * OpenSSL EVP_BytesToKey key derivation (MD5-based).
 * Derives key material by repeatedly hashing:
 *   D_0 = ""
 *   D_i = MD5(D_{i-1} || password || salt)
 *   key = D_1 || D_2 || ... (truncated to keyLen)
 *
 * Uses a pure-JS MD5 since Web Crypto does not support MD5.
 */
function evpBytesToKey(password: Uint8Array, salt: Uint8Array, keyLen: number): Uint8Array {
	const result = new Uint8Array(keyLen);
	let filled = 0;
	let prev = new Uint8Array(0);

	while (filled < keyLen) {
		const input = new Uint8Array(prev.length + password.length + salt.length);
		input.set(prev, 0);
		input.set(password, prev.length);
		input.set(salt, prev.length + password.length);

		prev = new Uint8Array(md5(input));

		const toCopy = Math.min(prev.length, keyLen - filled);
		result.set(prev.subarray(0, toCopy), filled);
		filled += toCopy;
	}

	return result;
}

// -- MD5 implementation (RFC 1321) ----------------------------------------
// web crypto does not support MD5, so we need a pure-JS implementation
// for the EVP_BytesToKey KDF used by traditional OpenSSL encrypted PEM files.

function md5(message: Uint8Array): Uint8Array {
	// pre-processing: add padding
	const msgLen = message.length;
	const bitLen = msgLen * 8;

	// message + 1 byte (0x80) + padding + 8 bytes (length)
	// padded length must be ≡ 56 (mod 64)
	const padLen = (56 - ((msgLen + 1) % 64) + 64) % 64;
	const padded = new Uint8Array(msgLen + 1 + padLen + 8);
	padded.set(message);
	padded[msgLen] = 0x80;

	// append original length in bits as 64-bit little-endian
	const view = new DataView(padded.buffer);
	view.setUint32(padded.length - 8, bitLen >>> 0, true);
	view.setUint32(padded.length - 4, 0, true); // upper 32 bits (always 0 for our use)

	// initial hash values
	let a0 = 0x67452301;
	let b0 = 0xefcdab89;
	let c0 = 0x98badcfe;
	let d0 = 0x10325476;

	// per-round shift amounts
	const s = [
		7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9,
		14, 20, 5, 9, 14, 20, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21,
		6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
	];

	// pre-computed T[i] = floor(2^32 * abs(sin(i + 1)))
	const K = new Uint32Array([
		0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee, 0xf57c0faf, 0x4787c62a, 0xa8304613, 0xfd469501,
		0x698098d8, 0x8b44f7af, 0xffff5bb1, 0x895cd7be, 0x6b901122, 0xfd987193, 0xa679438e, 0x49b40821,
		0xf61e2562, 0xc040b340, 0x265e5a51, 0xe9b6c7aa, 0xd62f105d, 0x02441453, 0xd8a1e681, 0xe7d3fbc8,
		0x21e1cde6, 0xc33707d6, 0xf4d50d87, 0x455a14ed, 0xa9e3e905, 0xfcefa3f8, 0x676f02d9, 0x8d2a4c8a,
		0xfffa3942, 0x8771f681, 0x6d9d6122, 0xfde5380c, 0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70,
		0x289b7ec6, 0xeaa127fa, 0xd4ef3085, 0x04881d05, 0xd9d4d039, 0xe6db99e5, 0x1fa27cf8, 0xc4ac5665,
		0xf4292244, 0x432aff97, 0xab9423a7, 0xfc93a039, 0x655b59c3, 0x8f0ccc92, 0xffeff47d, 0x85845dd1,
		0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1, 0xf7537e82, 0xbd3af235, 0x2ad7d2bb, 0xeb86d391,
	]);

	// process each 64-byte block
	const M = new Uint32Array(16);
	for (let offset = 0; offset < padded.length; offset += 64) {
		// break block into 16 little-endian uint32s
		for (let j = 0; j < 16; j++) {
			M[j] = view.getUint32(offset + j * 4, true);
		}

		let a = a0,
			b = b0,
			c = c0,
			d = d0;

		for (let i = 0; i < 64; i++) {
			let f: number, g: number;

			if (i < 16) {
				f = (b & c) | (~b & d);
				g = i;
			} else if (i < 32) {
				f = (d & b) | (~d & c);
				g = (5 * i + 1) % 16;
			} else if (i < 48) {
				f = b ^ c ^ d;
				g = (3 * i + 5) % 16;
			} else {
				f = c ^ (b | ~d);
				g = (7 * i) % 16;
			}

			const temp = d;
			d = c;
			c = b;
			const sum = (a + f + K[i]! + M[g]!) | 0;
			b = (b + ((sum << s[i]!) | (sum >>> (32 - s[i]!)))) | 0;
			a = temp;
		}

		a0 = (a0 + a) | 0;
		b0 = (b0 + b) | 0;
		c0 = (c0 + c) | 0;
		d0 = (d0 + d) | 0;
	}

	// produce the 16-byte digest (little-endian)
	const digest = new Uint8Array(16);
	const dv = new DataView(digest.buffer);
	dv.setUint32(0, a0, true);
	dv.setUint32(4, b0, true);
	dv.setUint32(8, c0, true);
	dv.setUint32(12, d0, true);
	return digest;
}

// -- RSA shared helpers ------------------------------------------------

/**
 * Import RSA parameters into Web Crypto and build the SshPrivateKey.
 * Uses rsa-sha2-256 (RSASSA-PKCS1-v1_5 with SHA-256) as the signing algorithm.
 */
async function importRsaKey(
	n: bigint,
	e: bigint,
	d: bigint,
	p: bigint,
	q: bigint,
	dp: bigint,
	dq: bigint,
	qi: bigint,
	publicKeyBlob: Uint8Array,
): Promise<SshPrivateKey> {
	// Web Crypto JWK import for RSA
	const jwk = {
		kty: 'RSA' as const,
		n: bigintToBase64url(n),
		e: bigintToBase64url(e),
		d: bigintToBase64url(d),
		p: bigintToBase64url(p),
		q: bigintToBase64url(q),
		dp: bigintToBase64url(dp),
		dq: bigintToBase64url(dq),
		qi: bigintToBase64url(qi),
	};

	const signingKey = await crypto.subtle.importKey(
		'jwk',
		jwk,
		{ name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
		false,
		['sign'],
	);

	return {
		algorithm: 'ssh-rsa',
		signingAlgorithm: 'rsa-sha2-256',
		publicKeyBlob,
		signingKey,
	};
}

/**
 * Build an SSH "ssh-rsa" public key blob from e and n.
 *
 * Format:
 *   string "ssh-rsa"
 *   mpint  e
 *   mpint  n
 */
function buildRsaPublicKeyBlob(e: bigint, n: bigint): Uint8Array {
	const w = new SshMessageWriter();
	w.writeString('ssh-rsa');
	w.writeMpint(e);
	w.writeMpint(n);
	return w.toUint8Array();
}

// -- DER / ASN.1 parsing -----------------------------------------------

type DerElement = {
	tag: number;
	contentStart: number;
	end: number;
};

/**
 * Read a DER element (tag + length + content) at the given offset.
 */
function derReadElement(data: Uint8Array, offset: number): DerElement {
	if (offset >= data.length) {
		throw new Error('DER: unexpected end of data');
	}

	const tag = data[offset]!;
	let pos = offset + 1;

	// read length
	let length: number;
	const firstLenByte = data[pos]!;
	pos++;

	if (firstLenByte < 0x80) {
		length = firstLenByte;
	} else {
		const numLenBytes = firstLenByte & 0x7f;
		length = 0;
		for (let i = 0; i < numLenBytes; i++) {
			length = (length << 8) | data[pos]!;
			pos++;
		}
	}

	return {
		tag,
		contentStart: pos,
		end: pos + length,
	};
}

/**
 * Read a DER SEQUENCE and return its content boundaries.
 */
function derReadSequence(data: Uint8Array, offset: number): DerElement {
	const el = derReadElement(data, offset);
	if (el.tag !== 0x30) {
		throw new Error(`DER: expected SEQUENCE (0x30), got 0x${el.tag.toString(16)}`);
	}
	return el;
}

/**
 * Read a DER INTEGER and return its value as a bigint.
 */
function derReadInteger(data: Uint8Array, offset: number): { value: bigint; end: number } {
	const el = derReadElement(data, offset);
	if (el.tag !== 0x02) {
		throw new Error(`DER: expected INTEGER (0x02), got 0x${el.tag.toString(16)}`);
	}

	const bytes = data.subarray(el.contentStart, el.end);
	let value = 0n;
	for (const byte of bytes) {
		value = (value << 8n) | BigInt(byte);
	}

	// handle sign bit — if high bit is set, it's negative (two's complement)
	if (bytes.length > 0 && bytes[0]! & 0x80) {
		value -= 1n << BigInt(bytes.length * 8);
	}

	return { value, end: el.end };
}

/**
 * Parse a PKCS#1 RSAPrivateKey DER structure into its component integers.
 * Returns [version, n, e, d, p, q, dp, dq, qi].
 */
function parsePkcs1Der(der: Uint8Array): bigint[] {
	const seq = derReadSequence(der, 0);
	const values: bigint[] = [];
	let pos = seq.contentStart;

	// read all 9 integers
	for (let i = 0; i < 9 && pos < seq.end; i++) {
		const { value, end } = derReadInteger(der, pos);
		values.push(value);
		pos = end;
	}

	return values;
}

// -- encoding helpers ---------------------------------------------------

/**
 * Convert a bigint to a base64url string (no padding) for JWK.
 * The output is unsigned big-endian, with no leading zero bytes
 * (JWK integers are always unsigned).
 */
function bigintToBase64url(value: bigint): string {
	if (value === 0n) return 'AA';

	const hex = value.toString(16);
	const padded = hex.length % 2 === 0 ? hex : '0' + hex;
	const bytes = new Uint8Array(padded.length / 2);
	for (let i = 0; i < padded.length; i += 2) {
		bytes[i / 2] = parseInt(padded.substring(i, i + 2), 16);
	}

	// standard base64, then convert to base64url
	const b64 = btoa(String.fromCharCode(...bytes));
	return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Decode a base64 string to Uint8Array.
 */
function base64Decode(b64: string): Uint8Array {
	const binary = atob(b64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return bytes;
}

/**
 * Extract and decode the base64 body from a PEM string.
 * Skips header/footer lines and any RFC 1421 headers (e.g. Proc-Type, DEK-Info).
 */
function decodePemBody(pem: string): Uint8Array {
	const lines = pem.split('\n');
	const body = lines
		.filter(
			(line) =>
				!line.startsWith('-----BEGIN') &&
				!line.startsWith('-----END') &&
				!line.match(/^[A-Za-z-]+:/) &&
				line.trim().length > 0,
		)
		.join('');
	return base64Decode(body);
}

function arraysEqual(a: Uint8Array, b: Uint8Array): boolean {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) return false;
	}
	return true;
}

/**
 * Decode a hex string to Uint8Array.
 */
function hexDecode(hex: string): Uint8Array {
	const bytes = new Uint8Array(hex.length / 2);
	for (let i = 0; i < hex.length; i += 2) {
		bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
	}
	return bytes;
}
